import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { 
  FolderIcon, 
  DocumentIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import { ResumeCheckpoint, FileProgressMap } from '../types/upload';
import { useMultipartUpload } from '../hooks/useMultipartUpload';
import { useTransfer } from '../contexts/TransferContext';
import { STORAGE_CLASSES } from '../constants/storageClasses';

/**
 * Calculates and formats the Estimated Time Remaining (ETR) for an upload.
 * Returns a string matching one of two patterns:
 *   "~{N}s remaining"    (when remaining < 60s)
 *   "~{M}m {S}s remaining" (when remaining >= 60s)
 * Returns empty string when any input is invalid or upload is complete.
 *
 * Requirements: 3.4, Property 11
 */
export function formatEtr(
  elapsedMs: number,
  bytesTransferred: number,
  totalBytes: number
): string {
  if (elapsedMs <= 0 || bytesTransferred <= 0 || totalBytes <= 0) return '';
  if (bytesTransferred >= totalBytes) return '';

  const bytesPerMs = bytesTransferred / elapsedMs;
  const remainingBytes = totalBytes - bytesTransferred;
  const remainingMs = remainingBytes / bytesPerMs;
  const remainingSecs = Math.max(0, Math.round(remainingMs / 1000));

  if (remainingSecs < 60) {
    return `~${remainingSecs}s remaining`;
  }

  const mins = Math.floor(remainingSecs / 60);
  const secs = remainingSecs % 60;
  return `~${mins}m ${secs}s remaining`;
}

/**
 * Calculates upload progress as an integer in [0, 100].
 * Requirements: 1.2, 3.1, Property 8
 */
export function calcProgress(loaded: number, total: number): number {
  return Math.min(100, Math.round((loaded / total) * 100));
}

interface FixedUploadDialogProps {
  bucketName: string;
  prefix: string;
  onUploadComplete: () => void;
  permissions: {
    upload: boolean;
    maxObjectSize: number;
  };
}

interface FileItem {
  file: File;
  path: string;
  size: string;
  type: string;
  icon: React.ReactNode;
}

interface StorageClassConfig {
  allowedStorageClasses: string[];
  defaultStorageClass: string;
  showStorageClassSelector: boolean;
}

const DEFAULT_STORAGE_CLASS_CONFIG: StorageClassConfig = {
  allowedStorageClasses: ['STANDARD'],
  defaultStorageClass: 'STANDARD',
  showStorageClassSelector: false,
};

const FixedUploadDialog: React.FC<FixedUploadDialogProps> = ({
  bucketName,
  prefix,
  onUploadComplete,
  permissions
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Progress tracking (Req 3.1)
  const [fileProgress, setFileProgress] = useState<FileProgressMap>({});

  // Batch counter (Req 3.3)
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // ETR display (Req 3.4)
  const [etrDisplay, setEtrDisplay] = useState<string>('');

  // Overall batch progress (0–100) across all files, derived from bytes transferred.
  const [overallProgress, setOverallProgress] = useState<number>(0);

  // Resume banner state (Req 5.9)
  const [resumeCheckpoints, setResumeCheckpoints] = useState<ResumeCheckpoint[]>([]);

  // File error tracking
  const [fileErrors, setFileErrors] = useState<Set<string>>(new Set());

  // Storage class config fetched on mount (Req 6.1)
  const [storageClassConfig, setStorageClassConfig] = useState<StorageClassConfig>(DEFAULT_STORAGE_CLASS_CONFIG);
  const [storageClassDropdownOpen, setStorageClassDropdownOpen] = useState(false);
  const storageClassDropdownRef = useRef<HTMLDivElement>(null);
  const storageClassTriggerRef = useRef<HTMLButtonElement>(null);
  const storageClassPanelRef = useRef<HTMLUListElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [selectedStorageClass, setSelectedStorageClass] = useState<string>('STANDARD');
  const [storageClassLoading, setStorageClassLoading] = useState<boolean>(false);

  // Abort controller for cancellation (Req 4.1)
  const abortControllerRef = useRef<AbortController | null>(null);

  // ETR calculation refs
  const uploadStartTimeRef = useRef<number>(0);
  const totalBytesRef = useRef<number>(0);
  const bytesTransferredRef = useRef<number>(0);

  // Resume: stores the checkpoint while user is re-selecting the file (Req 5.10)
  const pendingResumeCheckpointRef = useRef<ResumeCheckpoint | null>(null);

  const { uploadMultipart } = useMultipartUpload();
  const { registerTransfer } = useTransfer();

  // Fetch storage class config on mount (Req 6.1, 6.8, 8.3, 8.5)
  const fetchStorageClassConfig = useCallback(async () => {
    if (!permissions.upload) return;
    setStorageClassLoading(true);
    try {
      const folder = prefix || '';
      const response: StorageClassConfig = await apiCall(
        `/s3/storage-classes/${bucketName}${folder ? `?folder=${encodeURIComponent(folder)}` : ''}`
      );
      setStorageClassConfig(response);
      setSelectedStorageClass(response.defaultStorageClass || 'STANDARD');
    } catch (err) {
      console.error('Failed to fetch storage class config:', err);
      // Silently fall back to defaults (Req 6.8, 8.3)
      setStorageClassConfig(DEFAULT_STORAGE_CLASS_CONFIG);
      setSelectedStorageClass('STANDARD');
    } finally {
      setStorageClassLoading(false);
    }
  }, [bucketName, prefix, permissions.upload]);

  useEffect(() => {
    fetchStorageClassConfig();
  }, [fetchStorageClassConfig]);

  // Close storage class dropdown on outside click (Req 6.2)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = storageClassDropdownRef.current?.contains(target) ?? false;
      const insidePanel = storageClassPanelRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insidePanel) {
        setStorageClassDropdownOpen(false);
      }
    };
    if (storageClassDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [storageClassDropdownOpen]);

  // Mount-time checkpoint scan and cleanup (Req 5.8, 5.9)
  useEffect(() => {
    try {
      const CHECKPOINT_TTL_MS = 172_800_000; // 2 days
      const now = Date.now();
      // Map from uploadId → checkpoint for deduplication across stores
      const checkpointMap = new Map<string, ResumeCheckpoint>();

      // --- Scan sessionStorage ---
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!key || !key.startsWith(`mpu:${bucketName}:`)) continue;

        try {
          const raw = sessionStorage.getItem(key);
          if (!raw) continue;
          const checkpoint: ResumeCheckpoint = JSON.parse(raw);

          const age = now - new Date(checkpoint.initiatedAt).getTime();
          if (age > CHECKPOINT_TTL_MS) {
            // Expired — discard silently (Req 5.8, 12.1)
            sessionStorage.removeItem(key);
          } else {
            // Valid — add to map keyed by uploadId (Req 5.9, 12.2)
            checkpointMap.set(checkpoint.uploadId, checkpoint);
          }
        } catch {
          // Malformed checkpoint — discard silently
          if (key) sessionStorage.removeItem(key);
        }
      }

      // --- Scan localStorage — survives session-expiry redirect (Req 2.2) ---
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key || !key.startsWith(`mpu:${bucketName}:`)) continue;

          try {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const checkpoint: ResumeCheckpoint = JSON.parse(raw);

            const age = now - new Date(checkpoint.initiatedAt).getTime();
            if (age > CHECKPOINT_TTL_MS) {
              // Expired — discard from localStorage silently (Req 3.6)
              localStorage.removeItem(key);
            } else {
              // Merge: keep whichever has more completed parts (more progress = more recent)
              const existing = checkpointMap.get(checkpoint.uploadId);
              if (!existing) {
                checkpointMap.set(checkpoint.uploadId, checkpoint);
              } else {
                const existingCount = Object.keys(existing.completedParts).length;
                const lsCount = Object.keys(checkpoint.completedParts).length;
                if (lsCount > existingCount) {
                  checkpointMap.set(checkpoint.uploadId, checkpoint);
                }
              }
            }
          } catch {
            // Malformed checkpoint — discard silently
            if (key) localStorage.removeItem(key);
          }
        }
      } catch {
        // localStorage may be unavailable; continue with sessionStorage results
      }

      setResumeCheckpoints(Array.from(checkpointMap.values()));
    } catch {
      // If scan fails for any reason, mount normally (Req 5.8)
      // Silently retry on next mount
    }
  }, [bucketName]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to recursively process directory entries from drag and drop
  const processDirectoryEntry = useCallback(async (directoryEntry: FileSystemDirectoryEntry, path = ''): Promise<File[]> => {
    const files: File[] = [];
    
    return new Promise((resolve) => {
      const reader = directoryEntry.createReader();
      
      const readEntries = () => {
        reader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }
          
          for (const entry of entries) {
            const fullPath = path ? `${path}/${entry.name}` : entry.name;
            
            if (entry.isFile) {
              const fileEntry = entry as FileSystemFileEntry;
              const file = await new Promise<File>((resolveFile) => {
                fileEntry.file((file) => {
                  // Add webkitRelativePath to mimic folder input behavior
                  Object.defineProperty(file, 'webkitRelativePath', {
                    value: `${directoryEntry.name}/${fullPath}`,
                    writable: false
                  });
                  resolveFile(file);
                });
              });
              files.push(file);
            } else if (entry.isDirectory) {
              const subFiles = await processDirectoryEntry(entry as FileSystemDirectoryEntry, fullPath);
              files.push(...subFiles);
            }
          }
          
          // Continue reading if there might be more entries
          readEntries();
        });
      };
      
      readEntries();
    });
  }, []);

  const getFileIcon = (file: File): React.ReactNode => {
    if (file.type.startsWith('image/')) {
      return <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-green-600 text-xs font-bold">IMG</div>;
    } else if (file.type.startsWith('video/')) {
      return <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center text-purple-600 text-xs font-bold">VID</div>;
    } else if (file.type.startsWith('audio/')) {
      return <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-blue-600 text-xs font-bold">AUD</div>;
    } else if (file.type.includes('pdf')) {
      return <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center text-red-600 text-xs font-bold">PDF</div>;
    } else if (file.type.includes('text') || file.name.endsWith('.txt')) {
      return <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-gray-600 text-xs font-bold">TXT</div>;
    } else {
      return <DocumentIcon className="w-6 h-6 text-gray-400" />;
    }
  };

  const processFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const processedFiles: FileItem[] = [];
    const oversizedFileNames: string[] = [];

    fileArray.forEach(file => {
      // Check file size if limit is set
      if (permissions.maxObjectSize > 0 && file.size > permissions.maxObjectSize * 1024 * 1024) {
        oversizedFileNames.push(file.name);
        return; // Skip files that are too large
      }

      const path = (file as any).webkitRelativePath || file.name;
      processedFiles.push({
        file,
        path,
        size: formatFileSize(file.size),
        type: file.type || 'Unknown',
        icon: getFileIcon(file)
      });
    });

    // Show toast error for oversized files
    if (oversizedFileNames.length > 0) {
      const maxSize = permissions.maxObjectSize;
      if (oversizedFileNames.length === 1) {
        toast.error(`"${oversizedFileNames[0]}" exceeds the ${maxSize}MB file size limit`);
      } else if (oversizedFileNames.length <= 3) {
        toast.error(`${oversizedFileNames.length} files exceed the ${maxSize}MB limit: ${oversizedFileNames.join(', ')}`);
      } else {
        toast.error(`${oversizedFileNames.length} files exceed the ${maxSize}MB file size limit`);
      }
    }

    setSelectedFiles(prev => [...prev, ...processedFiles]);
  }, [permissions.maxObjectSize]);

  const handleFileSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFolderSelect = useCallback(() => {
    if (folderInputRef.current) {
      folderInputRef.current.click();
    }
  }, []);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    // --- Resume flow: check if a resume checkpoint is pending (Req 5.10) ---
    if (pendingResumeCheckpointRef.current && files && files.length > 0) {
      const checkpoint = pendingResumeCheckpointRef.current;
      pendingResumeCheckpointRef.current = null; // consume it immediately

      // Find a file whose name matches the checkpoint's file name
      const checkpointFileName = checkpoint.key.split('/').pop() || checkpoint.key;
      let matchedFile: File | null = null;
      for (let i = 0; i < files.length; i++) {
        if (files[i].name === checkpointFileName) {
          matchedFile = files[i];
          break;
        }
      }

      if (matchedFile) {
        const file = matchedFile;
        // Change 1: compute fileKey for progress map (matches per-file bar key convention)
        const fileKey = `${file.name}_${file.size}`;
        event.target.value = '';

        // Kick off the resume upload (Req 5.10)
        const controller = new AbortController();
        abortControllerRef.current = controller;

        uploadStartTimeRef.current = Date.now();
        totalBytesRef.current = checkpoint.fileSize;
        bytesTransferredRef.current = 0;
        // Change 2: initialize batch counters so header shows "0 of 1 files uploaded"
        setUploading(true);
        setUploadedCount(0);
        setTotalCount(1);
        // Change 3: initialize fileProgress so per-file bar appears immediately at 0%
        setFileProgress({ [fileKey]: 0 });
        setEtrDisplay('');

        const fileName = checkpointFileName;

        // Change 4: per-part byte accumulator for delta-based ETR (mirrors handleUpload multipart branch)
        const partBytesLoaded: Record<number, number> = {};

        uploadMultipart({
          bucketName: checkpoint.bucket,
          key: checkpoint.key,
          file,
          abortSignal: controller.signal,
          resumeCheckpoint: checkpoint,
          registerTransfer,
          // Change 5: delta-based onPartProgress — prevents byte double-counting and updates per-file bar
          onPartProgress: (partNumber, loaded, _total) => {
            const delta = loaded - (partBytesLoaded[partNumber] ?? 0);
            partBytesLoaded[partNumber] = loaded;
            if (delta > 0) {
              bytesTransferredRef.current = Math.min(
                bytesTransferredRef.current + delta,
                totalBytesRef.current
              );
            }
            const totalLoaded = Object.values(partBytesLoaded).reduce((a, b) => a + b, 0);
            setFileProgress(fp => ({ ...fp, [fileKey]: calcProgress(totalLoaded, totalBytesRef.current) }));
            const elapsed = Date.now() - uploadStartTimeRef.current;
            setEtrDisplay(formatEtr(elapsed, bytesTransferredRef.current, totalBytesRef.current));
          },
        }).then(() => {
          setResumeCheckpoints(prev => prev.filter(c => c.uploadId !== checkpoint.uploadId));
          // Change 6: finalize counter so header shows "1 of 1 files uploaded"
          setUploadedCount(1);
          setUploading(false);
          setEtrDisplay('');
          toast.success(`"${fileName}" upload completed`);
          onUploadComplete();
        }).catch(() => {
          setUploading(false);
          setEtrDisplay('');
        });
        return;
      } else {
        // File name didn't match — warn user and fall through to normal file add
        toast.error(`Please select "${checkpointFileName}" to resume the interrupted upload`);
        event.target.value = '';
        return;
      }
    }

    // --- Normal file selection flow ---
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input
    event.target.value = '';
  }, [processFiles, uploadMultipart, onUploadComplete, registerTransfer]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && permissions.upload) {
      setDragActive(true);
    }
  }, [uploading, permissions.upload]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!uploading && permissions.upload) {
      const items = e.dataTransfer.items;
      
      if (items && items.length > 0) {
        // Process DataTransferItems to handle folders properly
        const allFiles: File[] = [];
        
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            
            if (entry) {
              if (entry.isFile) {
                // Handle individual files
                const file = item.getAsFile();
                if (file) {
                  allFiles.push(file);
                }
              } else if (entry.isDirectory) {
                // Handle folders recursively
                const folderFiles = await processDirectoryEntry(entry as FileSystemDirectoryEntry);
                allFiles.push(...folderFiles);
              }
            }
          }
        }
        
        if (allFiles.length > 0) {
          processFiles(allFiles);
        }
      } else {
        // Fallback to regular files if items API is not available
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          processFiles(files);
        }
      }
    }
  }, [processFiles, processDirectoryEntry, uploading, permissions.upload]);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllFiles = useCallback(() => {
    setSelectedFiles([]);
    setSelectedStorageClass(storageClassConfig.defaultStorageClass);
  }, [storageClassConfig.defaultStorageClass]);

  const handleCancel = useCallback(() => {
    // Abort all in-flight XHR requests (Req 4.3)
    abortControllerRef.current?.abort();
    // Show cancel toast immediately — do NOT wait for abort confirmation (Req 4.3a)
    toast.error('Upload cancelled');
    // UI state updates immediately (Req 4.4) — uploading and ETR cleared
    setUploading(false);
    setEtrDisplay('');
    // selectedFiles is NOT cleared (Req 4.4, 14.1) — user can retry
    // fileProgress for in-flight files will be reset to 0 in handleUpload's catch branch
  }, []);

  const handleUpload = useCallback(async () => {
    if (!permissions.upload || selectedFiles.length === 0) return;

    // Create a fresh AbortController for this upload batch (Req 4.1)
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Initialize ETR tracking refs
    uploadStartTimeRef.current = Date.now();
    totalBytesRef.current = selectedFiles.reduce((sum, item) => sum + item.file.size, 0);
    bytesTransferredRef.current = 0;

    const MULTIPART_THRESHOLD = 104_857_600; // 100 MiB

    setUploading(true);
    setUploadedCount(0);
    setTotalCount(selectedFiles.length);
    setEtrDisplay('');
    setOverallProgress(0);
    setFileErrors(new Set());

    // Initialize progress map with 0 for all files
    const initialProgress: FileProgressMap = {};
    selectedFiles.forEach(item => {
      const fileKey = `${item.file.name}_${item.file.size}`;
      initialProgress[fileKey] = 0;
    });
    setFileProgress(initialProgress);

    let completedFilesCount = 0;
    const uploadedFiles: string[] = [];
    let totalUploadedSize = 0;

    for (const fileItem of selectedFiles) {
      // Check abort before each file
      if (controller.signal.aborted) break;

      const actualFileKey = `${fileItem.file.name}_${fileItem.file.size}`;

      try {
        const fileName = (fileItem.file as unknown as { webkitRelativePath?: string }).webkitRelativePath || fileItem.file.name;
        const fullPath = prefix + fileName;

        // --- Routing (Req 1.1, 1.5, 2.1, 11.1-11.4) ---

        // Case B: small file (< 100 MiB) — XHR PUT via existing presign endpoint
        if (fileItem.file.size < MULTIPART_THRESHOLD) {
          const cleanupTransfer = registerTransfer({
            id: `${bucketName}:${fullPath}:${Date.now()}`,
            type: 'upload',
            bucket: bucketName,
            key: fullPath,
          });
          try {
            // Get presigned URL for small file
            const uploadResponse: { uploadUrl: string } = await apiCall(`/s3/upload/${bucketName}`, {
              method: 'POST',
              body: JSON.stringify({
                fileName: fullPath,
                fileSize: fileItem.file.size,
                skipAudit: true,
                ...(storageClassConfig.showStorageClassSelector && { storageClass: selectedStorageClass }),
              }),
            });

            // XHR PUT to presigned URL (NO Content-Type — Req 1.4)
            await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              const onAbort = () => { xhr.abort(); reject(new Error('Upload cancelled')); };
              controller.signal.addEventListener('abort', onAbort, { once: true });

              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  const pct = calcProgress(event.loaded, event.total);
                  setFileProgress(prev => ({ ...prev, [actualFileKey]: pct }));
                  // Update ETR
                  bytesTransferredRef.current += event.loaded;
                  const elapsed = Date.now() - uploadStartTimeRef.current;
                  setEtrDisplay(formatEtr(elapsed, bytesTransferredRef.current, totalBytesRef.current));
                  // Update overall batch progress bar
                  setOverallProgress(calcProgress(bytesTransferredRef.current, totalBytesRef.current));
                }
              };

              xhr.onload = () => {
                controller.signal.removeEventListener('abort', onAbort);
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve();
                } else {
                  reject(new Error(`Upload failed with status ${xhr.status}`));
                }
              };
              xhr.onerror = () => { controller.signal.removeEventListener('abort', onAbort); reject(new Error('Network error')); };
              xhr.onabort = () => { controller.signal.removeEventListener('abort', onAbort); reject(new Error('Upload cancelled')); };

              // Do NOT set Content-Type (Req 1.4)
              xhr.open('PUT', uploadResponse.uploadUrl);
              xhr.send(fileItem.file);
            });
          } finally {
            cleanupTransfer();
          }

        } else {
          // Case C: large file (>= 100 MiB) — Multipart Upload Hook
          // Track bytes loaded per part so overall progress spans the whole file,
          // not just the current part (each part's XHR resets loaded to 0).
          const partBytesLoaded: Record<number, number> = {};
          const fileSize = fileItem.file.size;

          await uploadMultipart({
            bucketName,
            key: fullPath,
            file: fileItem.file,
            abortSignal: controller.signal,
            registerTransfer,
            onPartProgress: (partNumber, loaded, _total) => {
              // Accumulate bytes for ETR (delta from previous value for this part)
              const prev = partBytesLoaded[partNumber] ?? 0;
              const delta = loaded - prev;
              partBytesLoaded[partNumber] = loaded;
              if (delta > 0) {
                bytesTransferredRef.current = Math.min(
                  bytesTransferredRef.current + delta,
                  totalBytesRef.current
                );
              }
              const elapsed = Date.now() - uploadStartTimeRef.current;
              setEtrDisplay(formatEtr(elapsed, bytesTransferredRef.current, totalBytesRef.current));
              // Update overall batch progress bar
              setOverallProgress(calcProgress(bytesTransferredRef.current, totalBytesRef.current));
              // Overall file progress = sum of all parts' loaded bytes / total file size
              const totalLoaded = Object.values(partBytesLoaded).reduce((a, b) => a + b, 0);
              const pct = calcProgress(totalLoaded, fileSize);
              setFileProgress(fp => ({ ...fp, [actualFileKey]: pct }));
            },
          });
        }

        // Success
        setFileProgress(prev => ({ ...prev, [actualFileKey]: 100 }));
        completedFilesCount++;
        setUploadedCount(completedFilesCount);
        totalUploadedSize += fileItem.file.size;
        uploadedFiles.push(fullPath);

      } catch (error) {
        if (controller.signal.aborted) {
          // User cancelled — stop processing further files
          // Reset in-flight progress (Req 4.4, 14.3)
          setFileProgress(prev => ({ ...prev, [actualFileKey]: 0 }));
          break;
        }
        // Per-file failure
        setFileProgress(prev => ({ ...prev, [actualFileKey]: 0 }));
        setFileErrors(prev => new Set(prev).add(actualFileKey));
        // Duplicate-name conflict (409): the backend blocks uploads when a file
        // with the same name already exists in the folder. Show the specific
        // reason instead of a generic failure (large-file path is handled inside
        // the multipart hook).
        const message = error instanceof Error ? error.message : '';
        if (message.toLowerCase().includes('already exists')) {
          toast.error(`${fileItem.file.name}: a file with this name already exists in this folder`);
        } else {
          toast.error(`Failed to upload ${fileItem.file.name}`);
        }
      }
    }

    // Post-upload audit log (only if anything succeeded and not cancelled)
    if (uploadedFiles.length > 0 && !controller.signal.aborted) {
      try {
        await apiCall(`/s3/upload-complete/${bucketName}`, {
          method: 'POST',
          body: JSON.stringify({
            fileCount: uploadedFiles.length,
            totalSize: totalUploadedSize,
            folder: prefix || 'root',
            files: uploadedFiles,
          }),
        });
      } catch (err) {
        console.error('Failed to log upload audit:', err);
      }
    }

    setUploading(false);
    setEtrDisplay('');

    if (controller.signal.aborted) {
      // Cancel path — selectedFiles NOT cleared (Req 4.4, 14.1)
      return;
    }

    if (uploadedFiles.length > 0) {
      toast.success(`Successfully uploaded ${uploadedFiles.length} file(s)`);
      setSelectedFiles([]);
      onUploadComplete();
    }
  }, [selectedFiles, permissions.upload, bucketName, prefix, onUploadComplete, uploadMultipart, storageClassConfig, selectedStorageClass, registerTransfer]);

  const oversizedFiles = selectedFiles.filter(item => 
    permissions.maxObjectSize > 0 && item.file.size > permissions.maxObjectSize * 1024 * 1024
  );

  if (!permissions.upload) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-200 mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CloudArrowUpIcon className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Upload Files</h3>
            {uploading && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" role="status" aria-label="Loading"></div>
                  <span className="text-sm font-medium">
                    {uploadedCount} of {totalCount} files uploaded
                  </span>
                </div>
                {etrDisplay && (
                  <span className="text-xs text-gray-500">{etrDisplay}</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {/* Cancel button — shown while uploading (Req 4.2, 4.5) */}
            {uploading && (
              <button
                onClick={handleCancel}
                disabled={!uploading}
                className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                aria-label="Cancel Upload"
              >
                Cancel Upload
              </button>
            )}
            {selectedFiles.length > 0 && !uploading && (
              <button
                onClick={clearAllFiles}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Overall upload progress bar — spans the whole batch while uploading */}
        {uploading && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Overall progress</span>
              <span className="text-xs font-medium text-gray-600">{overallProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" role="progressbar" aria-valuenow={overallProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Overall upload progress">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Resume Banner (Req 5.9, 18.8) */}
        {resumeCheckpoints.length > 0 && (
          <div className="mb-4 space-y-2">
            {resumeCheckpoints.map((checkpoint) => (
              <div
                key={checkpoint.uploadId}
                className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <div className="flex items-center space-x-2 min-w-0">
                  <CloudArrowUpIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-700 font-medium truncate">
                    {checkpoint.key.split('/').pop() || checkpoint.key}
                  </span>
                  <span className="text-xs text-blue-500 flex-shrink-0">
                    ({Object.keys(checkpoint.completedParts).length}/{checkpoint.totalParts} parts done)
                  </span>
                </div>
                <div className="flex items-center space-x-3 flex-shrink-0 ml-3">
                  <button
                    onClick={() => {
                      if (uploading) return;
                      // Store the checkpoint and prompt the user to re-select the file.
                      // The actual resume is triggered in handleFileInputChange when the file matches (Req 5.10).
                      pendingResumeCheckpointRef.current = checkpoint;
                      fileInputRef.current?.click();
                    }}
                    disabled={uploading}
                    className="text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 px-3 py-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Resume {checkpoint.key.split('/').pop() || checkpoint.key}
                  </button>
                  <button
                    onClick={() => {
                      // Discard — remove checkpoint from both stores and hide banner entry (Req 5.11, 3.1)
                      const discardKey = `mpu:${checkpoint.bucket}:${encodeURIComponent(checkpoint.key)}`;
                      try {
                        sessionStorage.removeItem(discardKey);
                      } catch {
                        // fail silently
                      }
                      try {
                        localStorage.removeItem(discardKey);
                      } catch {
                        // fail silently
                      }
                      setResumeCheckpoints(prev => prev.filter(c => c.uploadId !== checkpoint.uploadId));
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Discard
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* File Selection Area */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
            dragActive
              ? 'border-blue-400 bg-blue-50 scale-105'
              : uploading
              ? 'border-gray-300 bg-gray-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {dragActive ? 'Drop files here!' : 'Drag & drop files or folders here'}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            or choose files using the buttons below
          </p>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleFileSelect}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentIcon className="w-4 h-4 mr-2" />
              Select Files
            </button>
            
            <button
              onClick={handleFolderSelect}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FolderIcon className="w-4 h-4 mr-2" />
              Select Folder
            </button>
          </div>

          {permissions.maxObjectSize > 0 && (
            <p className="text-xs text-gray-500 mt-4">
              Maximum file size: {permissions.maxObjectSize} MB
            </p>
          )}
        </div>

        {/* Storage Class Selector / Badge (Req 6.2, 6.3, 6.7, 6.9, 6.10) */}
        {!storageClassLoading && (selectedFiles.length > 0 || storageClassConfig.showStorageClassSelector) && (
          <div className="mt-4">
            {storageClassConfig.showStorageClassSelector ? (
              /* Custom styled selector — shown when admin enabled it */
              <div className="flex items-center space-x-3">
                <label
                  id="upload-storage-class-label"
                  className="text-sm font-medium text-gray-700 whitespace-nowrap"
                >
                  Storage class
                </label>
                <div
                  ref={storageClassDropdownRef}
                  className="relative flex-1 max-w-sm"
                  role="combobox"
                  aria-labelledby="upload-storage-class-label"
                  aria-expanded={storageClassDropdownOpen}
                  aria-haspopup="listbox"
                  aria-controls="upload-storage-class-listbox"
                >
                  {/* Trigger button */}
                  <button
                    ref={storageClassTriggerRef}
                    type="button"
                    onClick={() => {
                      if (uploading) return;
                      if (!storageClassDropdownOpen && storageClassTriggerRef.current) {
                        const rect = storageClassTriggerRef.current.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.left + window.scrollX,
                          width: rect.width,
                        });
                      }
                      setStorageClassDropdownOpen(prev => !prev);
                    }}
                    disabled={uploading}
                    aria-label="Select storage class"
                    className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-400 transition-colors"
                  >
                    <span className="font-medium text-gray-800 truncate">
                      {STORAGE_CLASSES.find(sc => sc.value === selectedStorageClass)?.label ?? selectedStorageClass}
                    </span>
                    <ChevronDownIcon
                      className={`h-4 w-4 text-gray-400 ml-2 flex-shrink-0 transition-transform duration-150 ${storageClassDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Dropdown panel — portalled to document.body to escape overflow:hidden ancestors */}
                  {storageClassDropdownOpen && dropdownPosition && createPortal(
                    <motion.ul
                      ref={storageClassPanelRef}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      role="listbox"
                      id="upload-storage-class-listbox"
                      aria-labelledby="upload-storage-class-label"
                      style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                      }}
                      className="z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
                    >
                      {STORAGE_CLASSES.filter(sc => storageClassConfig.allowedStorageClasses.includes(sc.value)).map(sc => {
                        const isSelected = sc.value === selectedStorageClass;
                        return (
                          <li
                            key={sc.value}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => {
                              setSelectedStorageClass(sc.value);
                              setStorageClassDropdownOpen(false);
                            }}
                            className={`flex items-start px-3 py-2.5 cursor-pointer select-none transition-colors ${
                              isSelected
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-900 hover:bg-gray-50'
                            }`}
                          >
                            <CheckIcon
                              className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'invisible'}`}
                            />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium leading-tight">{sc.label}</span>
                              <span className="text-xs text-gray-500 mt-0.5 leading-snug">{sc.description}</span>
                            </div>
                          </li>
                        );
                      })}
                    </motion.ul>,
                    document.body
                  )}
                </div>
              </div>
            ) : (
              /* Badge — shown when selector is hidden */
              <span className="text-xs text-gray-500">
                Storing as: {STORAGE_CLASSES.find(sc => sc.value === storageClassConfig.defaultStorageClass)?.label ?? storageClassConfig.defaultStorageClass}
              </span>
            )}
          </div>
        )}

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                Selected Files ({selectedFiles.length})
              </h4>
              <button
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                    Upload ({selectedFiles.length})
                  </>
                )}
              </button>
            </div>
            
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              {selectedFiles.map((item, index) => (
                <motion.div
                  key={`${item.file.name}_${item.file.size}_${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {item.icon}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.path}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.size} • {item.type || 'Unknown type'}
                      </p>
                      {/* Per-file progress bar (Req 3.2) */}
                      {uploading && fileProgress[`${item.file.name}_${item.file.size}`] !== undefined && (
                        <div className="mt-1">
                          {fileErrors.has(`${item.file.name}_${item.file.size}`) ? (
                            <div className="flex items-center space-x-1">
                              <div className="w-full bg-red-100 rounded-full h-1.5">
                                <div className="bg-red-500 h-1.5 rounded-full w-0"></div>
                              </div>
                              <span className="text-xs text-red-600 flex-shrink-0">Failed</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                  style={{ width: `${fileProgress[`${item.file.name}_${item.file.size}`] ?? 0}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0 w-8 text-right">
                                {fileProgress[`${item.file.name}_${item.file.size}`] ?? 0}%
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    disabled={uploading}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {oversizedFiles.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
              <p className="text-sm text-yellow-700">
                {oversizedFiles.length} file(s) exceed the {permissions.maxObjectSize} MB limit and will be skipped.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        {...({ webkitdirectory: '' } as any)}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default FixedUploadDialog;